import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Warehouse, Scissors, Shirt, CheckSquare, Save, Clock, User, Calendar, Package } from 'lucide-react';

export default function FloorDetailPage() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const factoryId = searchParams.get('factory') || '';
  const [month, setMonth] = useState(() => searchParams.get('month') || new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [godownForm, setGodownForm] = useState({ received: 0, checked: 0, relaxed: 0, issued: 0 });
  const [cuttingForm, setCuttingForm] = useState({ total_layered: 0, sent_to_stitching: 0 });
  const [stitchingForm, setStitchingForm] = useState({ sent_to_finishing: 0 });

  const loadData = useCallback(async () => {
    if (!factoryId || !orderId) return;
    setLoading(true);
    try {
      const res = await api.get(`/floor/${factoryId}/order/${orderId}?month=${month}`);
      setData(res.data);
      const g = res.data.godown;
      setGodownForm({ received: g.received, checked: g.checked, relaxed: g.relaxed, issued: g.issued });
      setCuttingForm({ total_layered: res.data.cutting.total_layered, sent_to_stitching: res.data.cutting.sent_to_stitching });
      setStitchingForm({ sent_to_finishing: res.data.stitching.sent_to_finishing });
    } catch (e) { toast.error('Failed to load'); }
    setLoading(false);
  }, [factoryId, orderId, month]);

  useEffect(() => { loadData(); }, [loadData]);

  const saveGodown = async () => {
    setSaving('godown');
    try {
      await api.post('/floor/godown', { order_id: orderId, factory_id: factoryId, month, ...godownForm });
      toast.success('Godown saved');
      await loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving('');
  };

  const saveCutting = async () => {
    setSaving('cutting');
    try {
      await api.post('/floor/cutting', { order_id: orderId, factory_id: factoryId, month, ...cuttingForm });
      toast.success('Cutting saved');
      await loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving('');
  };

  const saveStitching = async () => {
    setSaving('stitching');
    try {
      await api.post('/floor/stitching', { order_id: orderId, factory_id: factoryId, month, ...stitchingForm });
      toast.success('Stitching saved');
      await loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving('');
  };

  const backPath = window.location.pathname.includes('/internal/') ? '/dashboard/internal/floor' : '/dashboard/admin/floor';

  if (loading) return (
    <DashboardLayout>
      <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" /></div>
    </DashboardLayout>
  );

  if (!data) return (
    <DashboardLayout>
      <div className="text-center py-20 text-sm text-[#A1A1AA]">Order not found</div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div data-testid="floor-detail-page" className="space-y-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(backPath)} className="p-2 rounded-md hover:bg-[#F5F5F5] transition-colors" data-testid="floor-back-btn">
            <ArrowLeft className="w-5 h-5 text-[#52525B]" />
          </button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-semibold text-[#0A0A0A]">{data.pa} — {data.style}</h1>
            <div className="flex items-center gap-4 mt-1 text-sm text-[#52525B]">
              <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" />{data.buyer || '-'}</span>
              <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{data.delivery ? new Date(data.delivery).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}</span>
              <span className="inline-flex items-center gap-1"><Package className="w-3.5 h-3.5" />{data.quantity?.toLocaleString()} pcs</span>
            </div>
          </div>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 w-[160px] rounded-md text-sm" data-testid="detail-month-input" />
        </div>

        {/* GODOWN */}
        <DeptCard title="Godown" icon={Warehouse} color="bg-amber-50 text-amber-700" updated={data.godown.updated_at}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <StatBox label="Total Required" value={data.quantity} />
            <StatBox label="Received" value={data.godown.received} color="text-emerald-600" />
            <StatBox label="Issued to Cutting" value={data.godown.issued} color="text-blue-600" />
            <StatBox label="Pending" value={data.godown.pending} color="text-red-600" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-black/[0.06]">
            <FieldInput label="Fabric Received" value={godownForm.received} onChange={(v) => setGodownForm(p => ({ ...p, received: v }))} testid="gd-received" />
            <FieldInput label="Checked" value={godownForm.checked} onChange={(v) => setGodownForm(p => ({ ...p, checked: v }))} testid="gd-checked" />
            <FieldInput label="Relaxed" value={godownForm.relaxed} onChange={(v) => setGodownForm(p => ({ ...p, relaxed: v }))} testid="gd-relaxed" />
            <FieldInput label="Issued to Cutting" value={godownForm.issued} onChange={(v) => setGodownForm(p => ({ ...p, issued: v }))} testid="gd-issued" />
          </div>
          <div className="mt-4">
            <Button onClick={saveGodown} disabled={saving === 'godown'} size="sm" className="bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md text-sm h-9 px-5" data-testid="save-godown-btn">
              <Save className="w-3.5 h-3.5 mr-2" />{saving === 'godown' ? 'Saving...' : 'Save Godown'}
            </Button>
          </div>
        </DeptCard>

        {/* CUTTING */}
        <DeptCard title="Cutting" icon={Scissors} color="bg-blue-50 text-blue-700" updated={data.cutting.last_updated}>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-4">
            <StatBox label="Received (Godown)" value={data.cutting.received} sub="Auto" />
            <StatBox label="Layered" value={data.cutting.total_layered} color="text-violet-600" />
            <StatBox label="Cut (DPR)" value={data.cutting.cut} color="text-blue-600" sub="Auto" />
            <StatBox label="Sent to Stitching" value={data.cutting.sent_to_stitching} color="text-amber-600" />
            <StatBox label="Pending" value={data.cutting.pending} color="text-red-600" sub="Auto" />
          </div>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-black/[0.06]">
            <FieldInput label="Total Layered" value={cuttingForm.total_layered} onChange={(v) => setCuttingForm(p => ({ ...p, total_layered: v }))} testid="ct-layered" />
            <FieldInput label="Sent to Stitching" value={cuttingForm.sent_to_stitching} onChange={(v) => setCuttingForm(p => ({ ...p, sent_to_stitching: v }))} testid="ct-sent" />
          </div>
          <div className="mt-4">
            <Button onClick={saveCutting} disabled={saving === 'cutting'} size="sm" className="bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md text-sm h-9 px-5" data-testid="save-cutting-btn">
              <Save className="w-3.5 h-3.5 mr-2" />{saving === 'cutting' ? 'Saving...' : 'Save Cutting'}
            </Button>
          </div>
        </DeptCard>

        {/* STITCHING */}
        <DeptCard title="Stitching" icon={Shirt} color="bg-amber-50 text-amber-700" updated={data.stitching.last_updated}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <StatBox label="Received (Cutting)" value={data.stitching.received} sub="Auto" />
            <StatBox label="Stitched (DPR)" value={data.stitching.stitched} color="text-amber-600" sub="Auto" />
            <StatBox label="Sent to Finishing" value={data.stitching.sent_to_finishing} color="text-teal-600" />
            <StatBox label="Pending" value={data.stitching.pending} color="text-red-600" sub="Auto" />
          </div>
          <div className="grid grid-cols-1 max-w-xs gap-3 pt-4 border-t border-black/[0.06]">
            <FieldInput label="Sent to Finishing" value={stitchingForm.sent_to_finishing} onChange={(v) => setStitchingForm(p => ({ ...p, sent_to_finishing: v }))} testid="st-sent" />
          </div>
          <div className="mt-4">
            <Button onClick={saveStitching} disabled={saving === 'stitching'} size="sm" className="bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md text-sm h-9 px-5" data-testid="save-stitching-btn">
              <Save className="w-3.5 h-3.5 mr-2" />{saving === 'stitching' ? 'Saving...' : 'Save Stitching'}
            </Button>
          </div>
        </DeptCard>

        {/* FINISHING */}
        <DeptCard title="Finishing" icon={CheckSquare} color="bg-emerald-50 text-emerald-700" updated={data.finishing.last_updated}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <StatBox label="Received (Stitching)" value={data.finishing.received} sub="Auto" />
            <StatBox label="Finished (DPR)" value={data.finishing.finished} color="text-emerald-600" sub="Auto" />
            <StatBox label="Pending" value={data.finishing.pending} color="text-red-600" sub="Auto" />
          </div>
        </DeptCard>

        {/* Logs */}
        {data.logs && data.logs.length > 0 && (
          <div className="bg-white rounded-md border border-black/[0.06] p-6" data-testid="floor-logs-section">
            <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">Activity Logs</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {data.logs.map((log, i) => (
                <div key={log.id || i} className="flex items-start gap-3 text-xs py-2 border-b border-black/[0.03] last:border-0" data-testid={`floor-log-${i}`}>
                  <div className="w-6 h-6 rounded-full bg-[#F4F4F5] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User className="w-3 h-3 text-[#A1A1AA]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#0A0A0A]"><span className="font-semibold">{log.user_name}</span> updated <span className="font-medium text-[#52525B] capitalize">{log.department}</span></p>
                    <p className="text-[#A1A1AA] mt-0.5">{log.details}</p>
                  </div>
                  <span className="text-[#A1A1AA] flex-shrink-0 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {log.timestamp ? new Date(log.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function DeptCard({ title, icon: Icon, color, updated, children }) {
  return (
    <div className="bg-white rounded-md border border-black/[0.06] p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-md ${color} flex items-center justify-center`}><Icon className="w-4 h-4" /></div>
          <h2 className="text-base font-semibold text-[#0A0A0A]">{title}</h2>
        </div>
        {updated && <span className="text-[11px] text-[#A1A1AA] inline-flex items-center gap-1"><Clock className="w-3 h-3" />Updated {updated.slice(0, 10)}</span>}
      </div>
      {children}
    </div>
  );
}

function StatBox({ label, value, color = 'text-[#0A0A0A]', sub }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[#A1A1AA]">{label}</p>
      <p className={`font-heading text-2xl font-semibold mt-0.5 ${color}`}>{(value || 0).toLocaleString()}</p>
      {sub && <p className="text-[9px] text-[#D4D4D8] font-medium">{sub}</p>}
    </div>
  );
}

function FieldInput({ label, value, onChange, testid }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[#A1A1AA] mb-1">{label}</p>
      <Input type="number" min="0" value={value} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} className="h-9 rounded-md text-sm font-semibold text-center" data-testid={testid} />
    </div>
  );
}
